import { useState } from 'react'
import { SiteShell } from '../../ui/SiteShell'
import { WayIn } from '../../ui/WayIn'
import { FeatureDetail } from './FeatureDetail'
import { FEATURES, type Feature, PLANNED } from './features'
import { LuachExhibit } from './LuachExhibit'
import { PRODUCT_NAV } from './product-nav'
import { Point, Section } from './Section'
import { Steps } from './Steps'
import { ScheduledShot, SendRulesShot, SimchaShot } from './shots'
import { Tile } from './Tile'
import './marketing.css'

/**
 * ShulBase's front door. The platform's own is `PlatformHomePage`.
 *
 * ## Shape: breadth, then one exhibit
 *
 * The claim is **shul management** — the whole of it. So the page states the
 * breadth first, as a field of equal tiles where nothing outranks anything, and
 * then proves it can go deep by opening exactly ONE part all the way up.
 *
 * That order is deliberate and was got wrong before. Leading with the davening
 * schedule reads as "a zmanim program that also keeps a member list", which is a
 * smaller product than this is and the wrong one to be compared against. The
 * schedule is the best thing to *demonstrate* — it is the part with a visible
 * artifact and a hard problem behind it — but demonstrating a thing is not the
 * same as being that thing. It is the exhibit, not the pitch.
 *
 * Depth argued once also fixes what this page was before: nine equal bands each
 * arguing at length. Equal weight everywhere means no weight anywhere, and prose
 * down the page means the page cannot be scanned.
 *
 * ## Copy rules
 *
 * **A point is a heading and one sentence.** The argument behind it belongs in
 * the panel, which is where somebody who wants it goes looking. `Steps` takes
 * `line` as a string precisely so this cannot drift back into paragraphs.
 *
 * **Say what changes on a Tuesday afternoon** — not what the model looks like.
 * No anchors, no offsets, no "computed once, rendered everywhere". Those are
 * true, and they are ours.
 *
 * **The awkward case belongs in the evidence, not the pitch.** At one line each
 * every such product covers the same nouns, and what separates them is whether
 * they have met the week you are dreading — but that is shown, in the exhibit and
 * in the feature panels, rather than announced up top. Announced, it sells fear
 * and it shrinks the product to its calendar.
 *
 * The shots are the app's own components on invented data (`shots/`), never
 * images, for the reason given in that file.
 */
export function HomePage({ onLogin }: { onLogin?: () => void }) {
  const [open, setOpen] = useState<Feature | null>(null)

  return (
    <SiteShell nav={PRODUCT_NAV} onLogin={onLogin}>
      <Hero onLogin={onLogin} />
      <Everything onOpen={setOpen} />
      <Exhibit />
      <MessagingSection />
      <Assistant />
      {open ? (
        <FeatureDetail feature={open} onClose={() => setOpen(null)} />
      ) : null}
    </SiteShell>
  )
}

/* ── Hero ─────────────────────────────────────────────────────────────────── */

/**
 * The claim has two halves, and the headline carries the one a spreadsheet
 * cannot answer.
 *
 * - **Time** — set it up and stop maintaining it. No annual rebuild, no retyping,
 *   no year that catches you out.
 * - **Space** — nothing is a copy, so nothing can disagree.
 *
 * Space is the more obvious pitch and the weaker lead: "one place for everything"
 * is what every system says, and a reader has heard it. Time is the claim a pile
 * of spreadsheets structurally cannot make — they can be tidied into one
 * workbook, but somebody still rebuilds them every Elul — so it is the half that
 * separates this from the thing being replaced. Space follows in the lede, where
 * "nothing that disagrees" does its work in three words.
 *
 * Two rejected leads, recorded so they are not re-proposed:
 *
 * "The weeks you dread" sold relief from fear, and sold the calendar — one part
 * of this — as though it were the whole product.
 *
 * "Say it once. The shul runs on it." had no referent. Say *what* once? And the
 * second "it" pointed somewhere different from the first: grammatically at the
 * thing you said, but every reader takes it for the software. A headline cannot
 * spend its only sentence on two vague pronouns.
 */
function Hero({ onLogin }: { onLogin?: () => void }) {
  return (
    <section className="mk-hero">
      <div className="mk-measure mk-hero__inner">
        <p className="mk-label">Shul management</p>
        <h1 className="mk-hero__title">
          Set it up once. It runs every year after.
        </h1>
        <p className="mk-hero__lede">
          Members, dues, donations, messaging and every time you daven — nothing
          to rebuild, and nothing that disagrees.
        </p>
        <div className="mk-hero__actions">
          <WayIn onSignIn={onLogin} />
          <a className="mk-textlink" href="/shuls">
            See a shul’s pages
          </a>
        </div>
      </div>
    </section>
  )
}

/* ── The breadth ──────────────────────────────────────────────────────────── */

/**
 * Everything the platform runs, as equal tiles.
 *
 * Equal weight is the argument. A reader deciding whether one system can hold
 * their whole shul needs to see the whole list at once and find their own job in
 * it — and anything given more room here would read as "this is what it's really
 * for", which is the impression the page is built to avoid.
 *
 * A tile is a name and a line; the panel behind it carries the depth for whoever
 * wants it. What is NOT on the way is a roadmap item dressed as a feature, so
 * `PLANNED` stays named and nothing more.
 */
function Everything({ onOpen }: { onOpen: (f: Feature) => void }) {
  return (
    <Section
      id="features"
      label="What it runs"
      title="Members, donations, messaging, and the year."
    >
      <ul className="mk-tiles">
        {FEATURES.map((f) => (
          <li key={f.key}>
            <Tile
              onClick={() => onOpen(f)}
              icon={f.icon}
              name={f.title}
              line={f.lead}
            />
          </li>
        ))}
      </ul>

      <div className="mk-onway">
        <p className="mk-label">On the way</p>
        <ul className="mk-onway__list">
          {PLANNED.map((f) => (
            <li key={f.title}>{f.title}</li>
          ))}
        </ul>
      </div>
    </Section>
  )
}

/* ── The exhibit ─────────────────────────────────────────────────────────── */

/**
 * One part, all the way down.
 *
 * The section says out loud that this is an example, because a reader who thinks
 * the schedule IS the product has been told the wrong thing. What earns it the
 * space is that it is the hardest thing here to fake: a live sheet for a real
 * shul, in years that have not happened yet, with the reasoning attached.
 */
function Exhibit() {
  return (
    <Section
      id="year"
      label="One of them, in depth"
      // Not "say it once" again — the hero already made that claim, and the
      // exhibit's job is to make it checkable rather than restate it. This says
      // the surprising part out loud: the sheet below is not on file anywhere.
      title="Nobody wrote next year’s sheet."
    >
      <p className="mk-lede">
        Take the davening schedule — one of the parts above. Nothing below was
        authored for a particular year: pick a line to see the rule it comes
        from, then move between years and watch the times follow.
      </p>
      <LuachExhibit />
    </Section>
  )
}

/* ── Headline feature · messaging ─────────────────────────────────────────── */

function MessagingSection() {
  return (
    <Section
      label="Messaging"
      title="What you send is the schedule, not a copy of it."
    >
      <Steps
        items={[
          {
            title: 'Set against the occasion, not a date',
            line: 'It works out which Friday, and fills in that week’s times as it sends.',
            shot: <ScheduledShot />,
          },
          {
            title: 'It knows when to stay quiet',
            line: 'Silent on Shabbos and Yom Tov, and on the weeks a different sheet applies.',
            shot: <SendRulesShot />,
          },
        ]}
      />
    </Section>
  )
}

/* ── The assistant ────────────────────────────────────────────────────────── */

/**
 * The AI, argued from the job it does rather than from the fact of it.
 *
 * Everything claims AI now, so saying it persuades nobody. What is worth showing
 * is a task that was genuinely somebody's evening: an invitation arrives as a
 * photograph, in Hebrew, and has to become an email and a text that go to the
 * right people tonight. The model reads it, drafts both, and schedules them.
 *
 * Leading with the simcha does a second job for free. It is the one example that
 * cannot help demonstrating the rest of the platform on its way past — a person,
 * an occasion, an audience, two channels — so the section argues for the AI and
 * for the joined-up model at the same time, which is the page's whole thesis.
 *
 * The second claim is the one that matters commercially and is easy to miss: a
 * model rich enough to describe a real shul normally costs somebody days of
 * configuring, which is why most products flatten the model instead. Nothing is
 * flattened here, because the configuring is not done by a person.
 *
 * ## Why this one is not a rail
 *
 * A rail asks the reader to choose which point to open, which is right where the
 * points have separate evidence. Here they read one artifact together, so it is
 * shown whole and the claims sit under it. Two rails on one page also stop
 * reading as a device and start reading as the page's only idea.
 */
function Assistant() {
  return (
    <Section
      label="The assistant"
      title="Somebody sends you an invitation. That is the whole job."
    >
      <SimchaShot />

      {/*
       * The three claims have to be about the artifact directly above them. Two
       * of them were not: "fourteen exceptions stay fourteen exceptions" and
       * "a row you can read and change" are both about SCHEDULE RULES, sitting
       * under a shot of an invitation being read. A reader checks a claim against
       * the nearest evidence, and finding none is worse than a weaker claim.
       */}
      <div className="mk-pair mk-pair--three mk-pair--after">
        <Point title="A photograph is enough">
          It reads the invitation, Hebrew included — which is where most text
          recognition quietly gives up.
        </Point>
        <Point title="It reads the conventions too">
          Which couples are hosting, that a name in caps is not a name shouted,
          and what belongs in a subject line that has to survive an inbox.
        </Point>
        <Point title="It proposes; you send">
          Every draft is yours to read and change first. Nothing reaches the
          kehilla that you have not seen.
        </Point>
      </div>
    </Section>
  )
}
